/**
 * RestoredFiltersBanner — iter-213.9
 *
 * Tiny one-line affordance shown when a page's filter state was
 * rehydrated from localStorage AND included at least one non-empty
 * filter. Lets the user one-click reset everything if the persisted
 * filters aren't what they want right now.
 *
 * Pattern modeled after Notion / Linear: persistence happens silently
 * (no popup, no nag), but a subtle banner offers a clean escape hatch.
 *
 * Usage:
 *   const filters = useTransactionsFilters({ persistKey: "..." });
 *   <GroupedListView
 *     restoredBanner={
 *       <RestoredFiltersBanner
 *         visible={filters.restored && filters.hasActive}
 *         onClear={filters.clear}
 *         testid="receivables"
 *       />
 *     }
 *     ...
 *   />
 */
import { History, X } from "lucide-react";

export function RestoredFiltersBanner({ visible, onClear, testid = "filters", lang = "es" }) {
  if (!visible) return null;
  const msg = lang === "en"
    ? "Filters from your last visit are active"
    : lang === "pt"
    ? "Os filtros da sua última visita estão ativos"
    : "Tus filtros de la última visita siguen activos";
  const cta = lang === "en" ? "Clear all" : lang === "pt" ? "Limpar tudo" : "Limpiar todo";
  return (
    <div
      className="mb-3 flex items-center justify-between gap-2 rounded-xl border border-sky-200 dark:border-sky-900 bg-sky-50 dark:bg-sky-950/40 px-3 py-2"
      data-testid={`${testid}-restored-banner`}
    >
      <div className="flex items-center gap-2 text-xs text-sky-800 dark:text-sky-200">
        <History className="h-3.5 w-3.5 flex-shrink-0" />
        <span>{msg}</span>
      </div>
      <button
        type="button"
        onClick={onClear}
        data-testid={`${testid}-restored-clear`}
        className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 dark:text-sky-300 hover:text-sky-900 dark:hover:text-sky-100"
      >
        <X className="h-3 w-3" />
        {cta}
      </button>
    </div>
  );
}
